<template>
    <div id="settings-printers">
        <actions-menu>
            <actions-menu-item
                icon="arrow-left"
                :action="() => $router.push('/settings')"
                :v4="true"
                class="back"
                :label="trans('pages.printer_settings.back')"
            />
        </actions-menu>

        <template v-if="loading">
            <spinner-text>
                {{ trans('pages.printer_settings.loading') }}
            </spinner-text>
        </template>

        <template v-else-if="error">
            <div class="error">{{ error }}</div>
        </template>

        <template v-else>
            <div class="button-bar">
                <ui5-button @click="addNewPrinter">
                    <i class="fa fa-plus"></i>
                    {{ trans('pages.printer_settings.add_printer') }}
                </ui5-button>
                <ui5-button
                    @click="saveAllPrinters"
                    :disabled="!hasChanges"
                >
                    <i class="fa fa-save"></i>
                    {{ trans('pages.printer_settings.save_all') }}
                </ui5-button>
            </div>

            <div class="table-container">
                <table class="printer-table">
                    <thead>
                        <tr>
                            <th class="col-id">
                                {{ trans('pages.printer_settings.printer_id') }}
                            </th>
                            <th class="col-label">
                                {{
                                    trans(
                                        'pages.printer_settings.printer_label'
                                    )
                                }}
                            </th>
                            <th class="col-name">
                                {{
                                    trans('pages.printer_settings.printer_name')
                                }}
                            </th>
                            <th class="col-description">
                                {{
                                    trans('pages.printer_settings.description')
                                }}
                            </th>
                            <th class="col-workpost">
                                {{ trans('pages.printer_settings.workpost') }}
                            </th>
                            <th class="col-papertype">
                                {{ trans('pages.printer_settings.paper_type') }}
                            </th>
                            <th class="col-settings">
                                {{
                                    trans(
                                        'pages.printer_settings.printer_settings'
                                    )
                                }}
                            </th>
                            <th class="col-actions actions-cell">
                                {{ trans('pages.printer_settings.actions') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(printer, index) in printers"
                            :key="printer.id || 'new-' + index"
                            :class="{ 'deleted-row': printer.isDeleted }"
                        >
                            <td>
                                <span class="printer-id">
                                    {{ printer.id || 'NEW' }}
                                </span>
                            </td>
                            <td>
                                <ui5-input
                                    type="Text"
                                    :value="printer.label"
                                    :placeholder="
                                        trans(
                                            'pages.printer_settings.printer_label'
                                        )
                                    "
                                    @input="
                                        printer.label = $event.target.value;
                                        markAsChanged(printer);
                                    "
                                />
                            </td>
                            <td>
                                <ui5-input
                                    type="Text"
                                    :value="printer.name"
                                    :placeholder="
                                        trans(
                                            'pages.printer_settings.printer_name'
                                        )
                                    "
                                    @input="
                                        printer.name = $event.target.value;
                                        markAsChanged(printer);
                                    "
                                />
                            </td>
                            <td>
                                <ui5-input
                                    type="Text"
                                    :value="printer.description"
                                    :placeholder="
                                        trans(
                                            'pages.printer_settings.description'
                                        )
                                    "
                                    @input="
                                        printer.description =
                                            $event.target.value;
                                        markAsChanged(printer);
                                    "
                                />
                            </td>
                            <td>
                                <ui5-select
                                    @change="
                                        printer.workpost = $event.target.value;
                                        markAsChanged(printer);
                                    "
                                >
                                    <ui5-option
                                        value=""
                                        :selected="!printer.workpost"
                                    >
                                        {{
                                            trans(
                                                'pages.printer_settings.select_workpost'
                                            )
                                        }}
                                    </ui5-option>
                                    <ui5-option
                                        v-for="workpost in workposts"
                                        :key="workpost.ID"
                                        :value="workpost.ID"
                                        :selected="
                                            printer.workpost == workpost.ID
                                        "
                                    >
                                        {{ workpost.name }}
                                    </ui5-option>
                                </ui5-select>
                            </td>
                            <td>
                                <ui5-select
                                    @change="
                                        printer.papertype = $event.target.value;
                                        markAsChanged(printer);
                                    "
                                >
                                    <ui5-option
                                        value=""
                                        :selected="!printer.papertype"
                                    >
                                        {{
                                            trans(
                                                'pages.printer_settings.select_paper_type'
                                            )
                                        }}
                                    </ui5-option>
                                    <ui5-option
                                        v-for="papertype in papertypes"
                                        :key="papertype.ID"
                                        :value="papertype.ID"
                                        :selected="
                                            printer.papertype == papertype.ID
                                        "
                                    >
                                        {{ papertype.name }} ({{
                                            papertype.width
                                        }}x{{ papertype.height }}mm)
                                    </ui5-option>
                                </ui5-select>
                            </td>
                            <td>
                                <ui5-input
                                    type="Text"
                                    :value="printer.settings"
                                    :placeholder="
                                        trans(
                                            'pages.printer_settings.settings_placeholder'
                                        )
                                    "
                                    @input="
                                        printer.settings = $event.target.value;
                                        markAsChanged(printer);
                                    "
                                />
                            </td>
                            <td class="actions-cell">
                                <template v-if="!printer.isDeleted">
                                    <i
                                        class="fa fa-info-circle info-icon"
                                        @click="showPrinterInfo(printer)"
                                        :title="
                                            trans(
                                                'pages.printer_settings.show_info'
                                            )
                                        "
                                    />
                                    <img
                                        src="images/icons/trash-alt.svg"
                                        @click="deletePrinter(index)"
                                        :title="
                                            trans(
                                                'pages.printer_settings.delete_printer'
                                            )
                                        "
                                        class="delete-icon"
                                    />
                                </template>
                                <i
                                    v-else
                                    class="fa fa-recycle undo-icon"
                                    @click="undoDelete(index)"
                                    :title="
                                        trans(
                                            'pages.printer_settings.undo_delete'
                                        )
                                    "
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <!-- Printer Info Dialog -->
        <ui5-dialog
            id="printer-info-dialog"
            :open="showInfoDialog"
            @ui5-after-close="closeInfoDialog"
            :header-text="dialogTitle"
        >
            <div
                v-if="selectedPrinterInfo"
                class="printer-info-content"
            >
                <div class="info-section">
                    <h3>
                        {{ trans('pages.printer_settings.current_settings') }}
                    </h3>
                    <p>
                        <strong>
                            {{
                                trans(
                                    'pages.printer_settings.printer_settings'
                                )
                            }}:
                        </strong>
                        <code
                            v-if="currentSettings"
                            v-text="currentSettings"
                        ></code>
                        <span
                            v-else
                            class="no-settings"
                        >
                            {{ trans('pages.printer_settings.no_settings') }}
                        </span>
                    </p>
                </div>

                <div
                    v-if="selectedPrinterInfo.cups_info"
                    class="info-section"
                >
                    <h3>{{ trans('pages.printer_settings.cups_info') }}</h3>
                    <table class="cups-info-table">
                        <tr
                            v-for="(
                                value, key
                            ) in selectedPrinterInfo.cups_info"
                            :key="key"
                        >
                            <td>
                                <strong>{{ key }}:</strong>
                            </td>
                            <td>{{ value }}</td>
                        </tr>
                    </table>
                </div>

                <div
                    v-if="
                        selectedPrinterInfo.cupsFiles &&
                        selectedPrinterInfo.cupsFiles.length
                    "
                    class="info-section"
                >
                    <h3>{{ trans('pages.printer_settings.cups_files') }}</h3>
                    <ul class="cups-files-list">
                        <li
                            v-for="file in selectedPrinterInfo.cupsFiles"
                            :key="file"
                        >
                            <i class="fa fa-download download-icon"></i>
                            <a
                                :href="getCupsFileUrl(file)"
                                target="_blank"
                            >
                                {{ file }}
                            </a>
                        </li>
                    </ul>
                </div>

                <div
                    v-if="selectedPrinterInfo.recommendedSettings"
                    class="info-section"
                >
                    <h3>
                        {{
                            trans('pages.printer_settings.recommended_settings')
                        }}
                    </h3>
                    <p>
                        <code
                            v-text="selectedPrinterInfo.recommendedSettings"
                        ></code>
                    </p>
                </div>
            </div>

            <div
                slot="footer"
                class="dialog-footer"
            >
                <ui5-button @click="closeInfoDialog">
                    {{ trans('pages.printer_settings.close') }}
                </ui5-button>
            </div>
        </ui5-dialog>
    </div>
</template>

<script>
import ActionsMenu from '../components/ActionsMenu';
import ActionsMenuItem from '../components/ActionsMenuItem';
import SpinnerText from '../components/loader/SpinnerText';
import localizationMixin from '../mixins/localization';
import printersApi from '../api/printers';
import workpostApi from '../api/workpost';

export default {
    mixins: [localizationMixin],
    components: {
        ActionsMenu,
        ActionsMenuItem,
        SpinnerText,
    },
    data() {
        return {
            printers: [],
            workposts: [],
            papertypes: [],
            loading: true,
            error: null,
            hasChanges: false,
            originalPrinters: [],
            showInfoDialog: false,
            selectedPrinterInfo: null,
            debugData: null,
        };
    },
    computed: {
        dialogTitle() {
            if (!this.selectedPrinterInfo) return 'Printer Information';

            const parts = [];
            if (this.selectedPrinterInfo.label) {
                parts.push(this.selectedPrinterInfo.label);
            }
            if (this.selectedPrinterInfo.description) {
                parts.push(this.selectedPrinterInfo.description);
            }

            return parts.length > 0 ? parts.join(' - ') : 'Printer Information';
        },

        currentSettings() {
            if (!this.selectedPrinterInfo) return null;

            // Check current settings from the table first
            if (
                this.selectedPrinterInfo.settings &&
                this.selectedPrinterInfo.settings.trim()
            ) {
                return this.selectedPrinterInfo.settings.trim();
            }

            // Fallback to backup data settings
            if (
                this.selectedPrinterInfo.printersettings &&
                this.selectedPrinterInfo.printersettings.trim()
            ) {
                return this.selectedPrinterInfo.printersettings.trim();
            }

            return null;
        },
    },
    async mounted() {
        await this.loadData();
    },
    methods: {
        async loadData() {
            try {
                this.loading = true;
                this.error = null;

                const [
                    printersResponse,
                    workpostsResponse,
                    papertypesResponse,
                ] = await Promise.all([
                    printersApi.fetchAll(true),
                    workpostApi.listWorkposts(),
                    printersApi.fetchPapertypes(),
                    this.fetchDebugData(),
                ]);

                this.printers = printersResponse.data.data.map((printer) => ({
                    id: printer.id,
                    label: printer.label || '',
                    name: printer.name || '',
                    description: printer.description || '',
                    workpost: printer.workpost || '',
                    papertype: printer.papertype || '',
                    settings: printer.settings || '',
                    isNew: false,
                    isModified: false,
                    isDeleted: false,
                }));

                this.workposts = workpostsResponse.data.data || [];
                this.papertypes = papertypesResponse.data.data || [];

                this.originalPrinters = JSON.parse(
                    JSON.stringify(this.printers)
                );
                this.loading = false;
            } catch (err) {
                console.error('Error loading data:', err);
                this.error = this.trans('pages.printer_settings.error_loading');
                this.loading = false;
            }
        },

        addNewPrinter() {
            this.printers.push({
                id: null,
                label: '',
                name: '',
                description: '',
                workpost: '',
                papertype: '',
                settings: '',
                isNew: true,
                isModified: false,
                isDeleted: false,
            });
            this.markHasChanges();
        },

        deletePrinter(index) {
            const printer = this.printers[index];
            if (printer.isNew) {
                this.printers.splice(index, 1);
            } else {
                printer.isDeleted = true;
            }
            this.markHasChanges();
        },

        undoDelete(index) {
            const printer = this.printers[index];
            printer.isDeleted = false;
            this.markHasChanges();
        },

        markAsChanged(printer) {
            printer.isModified = true;
            this.markHasChanges();
        },

        markHasChanges() {
            this.hasChanges = true;
        },

        async saveAllPrinters() {
            try {
                const changedPrinters = this.printers
                    .filter((p) => p.isNew || p.isModified || p.isDeleted)
                    .map((printer) => ({
                        id: printer.id,
                        label: printer.label,
                        name: printer.name,
                        description: printer.description,
                        workpost: printer.workpost || null,
                        papertype: printer.papertype || null,
                        settings: printer.settings,
                        operation: printer.isDeleted
                            ? 'delete'
                            : printer.isNew
                            ? 'create'
                            : 'update',
                    }));

                if (changedPrinters.length === 0) {
                    return;
                }

                await printersApi.batchUpdate(changedPrinters);

                // Reload data to get fresh state
                await this.loadData();
                this.hasChanges = false;

                // Refresh printers store to keep it in sync
                this.$store.dispatch('printers/fetchPrinters');

                // Show success message or notification here if needed
            } catch (err) {
                console.error('Error saving printers:', err);
                this.error = this.trans('pages.printer_settings.error_saving');
            }
        },

        async fetchDebugData() {
            try {
                const response = await printersApi.fetchDebugData();
                this.debugData = response.data;
            } catch (error) {
                console.error('Error loading debug data:', error);
                this.debugData = [];
            }
        },

        showPrinterInfo(printer) {
            // Find debug info for this printer
            const debugInfo = this.debugData?.find((d) => d.id == printer.id);

            this.selectedPrinterInfo = {
                ...printer,
                ...debugInfo,
            };

            this.showInfoDialog = true;
        },

        closeInfoDialog() {
            this.showInfoDialog = false;
            this.selectedPrinterInfo = null;
        },

        getCupsFileUrl(filename) {
            return `/printer/downloadCupsFile/${encodeURIComponent(filename)}`;
        },
    },
};
</script>

<style
    scoped
    lang="scss"
>
.button-bar {
    display: flex;
    gap: 12px;
    margin-bottom: 1rem;
}

.table-container {
    overflow-x: auto;
}

.printer-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.printer-table {
    th,
    td {
        padding: 12px;
        text-align: left;
        border-bottom: 1px solid #e5e5e5;
        vertical-align: middle;

        &.actions-cell {
            text-align: center;
        }
    }

    th {
        background-color: #f8f9fa;
        font-weight: 600;
        color: #333;
    }

    tr:hover {
        background-color: #f8f9fa;
    }

    ui5-input,
    ui5-select {
        width: 100%;
    }
}

/* Column widths */
.col-id {
    width: 5%;
}
.col-label {
    width: 8%;
}
.col-name {
    width: 15%;
}
.col-description {
    width: 27%;
}
.col-workpost {
    width: 12%;
}
.col-papertype {
    width: 12%;
}
.col-settings {
    width: 16%;
}
.col-actions {
    width: 5%;
}

.printer-id {
    font-weight: 600;
    color: #666;
}

.delete-icon {
    cursor: pointer;
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s;
}

.delete-icon:hover {
    opacity: 1;
}

.undo-icon {
    cursor: pointer;
    font-size: 18px;
    color: #28a745;
    opacity: 0.8;
    transition: opacity 0.2s;

    &:hover {
        opacity: 1;
    }
}

.deleted-row {
    opacity: 0.6;
    background-color: #ffeaea !important;

    &:hover {
        background-color: #ffeaea !important;
    }
}

.error {
    background: #f8d7da;
    color: #721c24;
    padding: 12px;
    border: 1px solid #f5c6cb;
    border-radius: 4px;
    margin: 1rem 0;
}

.info-icon {
    color: #007bff;
    cursor: pointer;
    font-size: 16px;
    margin-right: 8px;

    &:hover {
        color: #0056b3;
    }
}

.printer-info-content {
    .info-section {
        margin-bottom: 20px;

        h3 {
            margin: 0 0 10px 0;
            color: #333;
            font-size: 16px;
        }

        ul {
            margin: 8px 0;
            padding-left: 20px;
        }

        li {
            margin-bottom: 4px;
        }

        code {
            background: #f8f9fa;
            padding: 2px 6px;
            border-radius: 3px;
            font-family: monospace;
            color: #e83e8c;
            white-space: pre-wrap;
        }

        a {
            color: #007bff;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }

        .no-settings {
            color: #6c757d;
            font-style: italic;
        }
    }

    .cups-info-table {
        width: 100%;
        border-collapse: collapse;
        margin: 12px 0;

        td {
            padding: 8px 12px;
            border-bottom: 1px solid #e5e5e5;
            vertical-align: top;

            &:first-child {
                width: 200px;
                background-color: #f8f9fa;
            }
        }
    }

    .cups-files-list {
        li {
            display: flex;
            align-items: center;
            padding: 4px 0;

            .download-icon {
                color: #28a745;
                margin-right: 8px;
                font-size: 14px;
            }
        }
    }
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
</style>
